/**
 * Public Screen — the room's focal display during a live simulation.
 * Route: /screen/:id (no auth required — display only)
 *
 * Layout: Full screen, dark, no navigation.
 * Top: Round + Phase + Timer + Sim time mapping
 * Center: Global map (2:1) with attacks + units
 * Right: Doomsday indices (rotating with theater maps)
 * Bottom-left: Columbia vs Cathay power balance
 * Bottom: News ticker (scrolling events)
 */

import { useEffect, useState, useCallback, useRef } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { getSimRun, type SimRun } from '@/lib/queries'
import { ScreenB } from '@/components/public/ScreenB'

/* -------------------------------------------------------------------------- */
/*  Types                                                                     */
/* -------------------------------------------------------------------------- */

interface SimState {
  status: string
  current_round: number
  current_phase: string
  phase_started_at: string | null
  phase_duration_seconds: number | null
  max_rounds: number
}

interface PublicEvent {
  id: string
  event_type: string
  summary: string
  role_name: string | null
  category: string | null
  country_code: string | null
  payload: Record<string, unknown> | null
  created_at: string
  visibility?: string | null
}

interface DoomsdayIndices {
  geopolitical_tension: number
  economic_health: number
  nuclear_danger: number
  ai_race: number
  prev_geopolitical_tension: number
  prev_economic_health: number
  prev_nuclear_danger: number
  prev_ai_race: number
  outlook_tension: string
  outlook_crisis: string
  outlook_doom: string
  outlook_agi: string
}

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                   */
/* -------------------------------------------------------------------------- */

const ROUND_TO_DATE: Record<number, string> = {
  0: 'Pre-Sim',
  1: 'H2 2026',
  2: 'H1 2027',
  3: 'H2 2027',
  4: 'H1 2028',
  5: 'H2 2028',
  6: 'H1 2029',
  7: 'H2 2029',
  8: 'H1 2030',
}

function formatTimer(seconds: number): string {
  if (seconds < 0) return 'OVERTIME'
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

function phaseLabel(phase: string): string {
  switch (phase) {
    case 'A': return 'NEGOTIATIONS'
    case 'B': return 'BREAK'
    case 'pre': return 'PRE-START'
    case 'post': return 'POST-SIM'
    default: return phase.toUpperCase()
  }
}

function trendArrow(current: number, prev: number): string {
  if (current > prev) return '\u2191'  // ↑
  if (current < prev) return '\u2193'  // ↓
  return '\u2192'  // →
}

function trendColor(current: number, prev: number, dangerUp: boolean = true): string {
  const rising = current > prev
  if (dangerUp) return rising ? 'text-danger' : current < prev ? 'text-success' : 'text-text-secondary'
  return rising ? 'text-success' : current < prev ? 'text-danger' : 'text-text-secondary'
}

/* PUBLIC event types for the news ticker.
 * Only events that are publicly visible in the simulation world.
 * Covert ops (intelligence, sabotage, propaganda, election_meddling)
 * are SECRET — they only appear if the engine marks them as revealed. */
const PUBLIC_NEWS_EVENTS = new Set([
  // Military — big news
  'ground_attack', 'air_strike', 'naval_combat', 'naval_bombardment',
  'launch_missile_conventional', 'nuclear_launch', 'nuclear_test',
  'naval_blockade',
  // Political — dramatic
  'declare_war', 'change_leader_removal', 'change_leader_elected',
  'martial_law', 'arrest',
  // Diplomatic — public
  'public_statement',
  'agreement_activated',   // only activated agreements (not proposals)
  'transaction_executed',  // only completed deals (not proposals)
  // Detected covert ops — if caught, it's news
  'assassination_attributed', 'sabotage_detected_attributed',
  // Moderator broadcasts
  'moderator_broadcast',
])

/* -------------------------------------------------------------------------- */
/*  Component                                                                 */
/* -------------------------------------------------------------------------- */

export function PublicScreen() {
  const { id: simId } = useParams<{ id: string }>()

  const [simRun, setSimRun] = useState<SimRun | null>(null)
  const [simState, setSimState] = useState<SimState | null>(null)
  const [events, setEvents] = useState<PublicEvent[]>([])
  const [remaining, setRemaining] = useState<number | null>(null)
  const [indices, setIndices] = useState<DoomsdayIndices>({
    geopolitical_tension: 5, economic_health: 6, nuclear_danger: 4, ai_race: 4,
    prev_geopolitical_tension: 5, prev_economic_health: 6, prev_nuclear_danger: 4, prev_ai_race: 4,
    outlook_tension: 'stable', outlook_crisis: 'stable', outlook_doom: 'stable', outlook_agi: 'stable',
  })
  const [colPower, setColPower] = useState(55)
  const [catPower, setCatPower] = useState(45)
  const [powerHistory, setPowerHistory] = useState<{round_num: number; columbia: number; cathay: number}[]>([])
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const tickerRef = useRef<HTMLDivElement>(null)
  // News ticker pagination — see SPEC_NEWS_TICKER.md
  // tickerPage cycles through pages of TICKER_PAGE_SIZE events every TICKER_PAGE_DURATION_MS.
  // pageCount is recomputed each render and cached in a ref so the interval reads the latest value
  // without restarting when new events arrive via Realtime.
  const [tickerPage, setTickerPage] = useState(0)
  const tickerPageCountRef = useRef(1)
  // Map refresh key — changes when phase changes to force iframe reload
  const [mapKey, setMapKey] = useState(0)
  // Nuclear alert banner state
  const [nuclearAlert, setNuclearAlert] = useState<{country:string, testType:string}|null>(null)
  const nuclearAlertTimerRef = useRef<ReturnType<typeof setTimeout>|null>(null)
  // Nuclear launch banner state (dramatic, 60s display)
  const [nuclearLaunchAlert, setNuclearLaunchAlert] = useState<{country:string, missiles:number, targets:string}|null>(null)
  const nuclearLaunchTimerRef = useRef<ReturnType<typeof setTimeout>|null>(null)
  // Nuclear flight banner (from nuclear_actions realtime)
  const [nuclearFlightAction, setNuclearFlightAction] = useState<{country:string,missiles:number,timerStart:number,duration:number}|null>(null)
  const [flightCountdown, setFlightCountdown] = useState<number|null>(null)
  // Screen A/B toggle: 'a' = map+gauges, 'b' = numbers+events, 'rotate' = auto-switch
  // Support ?screen=b or #b in URL for direct Screen B access
  const [searchParams] = useSearchParams()
  const initialScreen = searchParams.get('screen') === 'b' || window.location.hash === '#b' ? 'b' : 'a'
  const [screenMode, setScreenMode] = useState<'a' | 'b' | 'rotate'>(initialScreen)
  const [activeScreen, setActiveScreen] = useState<'a' | 'b'>(initialScreen)
  const [screenBFrozen, setScreenBFrozen] = useState(false)
  const [showToggle, setShowToggle] = useState(true)
  const toggleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Layout zoom modes for big-screen setup. Persisted in localStorage so a
  // refresh on the projector machine restores whatever the moderator picked.
  // Screen A: 'full' (map + right panel + ticker) -> 'map-ticker' (map + ticker,
  // no right panel) -> 'map' (only map, no right panel, no ticker).
  // Screen B: 'full' (stats + events + footer) -> 'stats' (stats only) ->
  // 'events' (events grid only).
  type LayoutA = 'full' | 'map-ticker' | 'map'
  type LayoutB = 'full' | 'stats' | 'events'
  const [layoutA, _setLayoutA] = useState<LayoutA>(() => {
    const v = typeof window !== 'undefined' ? localStorage.getItem('publicScreen.layoutA') : null
    return (v === 'map-ticker' || v === 'map') ? v : 'full'
  })
  const [layoutB, _setLayoutB] = useState<LayoutB>(() => {
    const v = typeof window !== 'undefined' ? localStorage.getItem('publicScreen.layoutB') : null
    return (v === 'stats' || v === 'events') ? v : 'full'
  })
  const setLayoutA = (v: LayoutA) => { _setLayoutA(v); try { localStorage.setItem('publicScreen.layoutA', v) } catch { /* noop */ } }
  const setLayoutB = (v: LayoutB) => { _setLayoutB(v); try { localStorage.setItem('publicScreen.layoutB', v) } catch { /* noop */ } }

  // Screen A main map can show Global view or zoom into one of the two
  // regional conflict theaters. Switcher overlays bottom-left of the map
  // iframe (per playtest feedback 2026-05-24). Persisted in localStorage so
  // the projector restores the moderator's choice on refresh.
  type Theater = 'global' | 'eastern_ereb' | 'mashriq'
  const [mapTheater, _setMapTheater] = useState<Theater>(() => {
    const v = typeof window !== 'undefined' ? localStorage.getItem('publicScreen.mapTheater') : null
    return (v === 'eastern_ereb' || v === 'mashriq') ? v : 'global'
  })
  const setMapTheater = (v: Theater) => { _setMapTheater(v); try { localStorage.setItem('publicScreen.mapTheater', v) } catch { /* noop */ } }

  /* Load data ------------------------------------------------------------- */
  const loadData = useCallback(async () => {
    if (!simId) return
    try {
      const run = await getSimRun(simId)
      if (!run) return
      setSimRun(run)
      setSimState({
        status: run.status,
        current_round: run.current_round,
        current_phase: run.current_phase,
        phase_started_at: run.phase_started_at || run.started_at,
        phase_duration_seconds: run.phase_duration_seconds || (run.schedule?.phase_a_minutes ? run.schedule.phase_a_minutes * 60 : 3600),
        max_rounds: run.max_rounds,
      })

      // Load significant events
      const { data: evts } = await supabase
        .from('observatory_events')
        .select('id, event_type, summary, role_name, category, country_code, payload, created_at, visibility, published_at')
        .eq('sim_run_id', simId)
        .order('created_at', { ascending: false })
        .limit(30)
      // Load recent moderator broadcasts and merge with events
      const { data: broadcasts } = await supabase
        .from('public_broadcasts')
        .select('id, content, created_at')
        .eq('sim_run_id', simId)
        .order('created_at', { ascending: false })
        .limit(5)
      const broadcastEvents: PublicEvent[] = (broadcasts ?? []).map((bc) => ({
        id: bc.id,
        event_type: 'moderator_broadcast',
        summary: bc.content,
        role_name: null,
        category: 'system',
        country_code: null,
        payload: null,
        created_at: bc.created_at,
        visibility: 'public',
      }))
      setEvents([...broadcastEvents, ...(evts ?? [])] as PublicEvent[])

      // Load world indicators (latest 2 published for current vs previous comparison)
      const { data: latestInds } = await supabase
        .from('world_indicators')
        .select('*')
        .eq('sim_run_id', simId)
        .eq('status', 'published')
        .order('created_at', { ascending: false })
        .limit(2)
      if (latestInds?.[0]) {
        const ind = latestInds[0]
        const prevInd = latestInds[1] || null
        const gt = (ind.geopolitical_tension as Record<string, number>) || {}
        const ec = (ind.economic_crisis as Record<string, number>) || {}
        const dc = (ind.doomsday_clock as Record<string, number>) || {}
        const da = (ind.distance_to_agi as Record<string, number>) || {}
        const pgt = prevInd ? ((prevInd.geopolitical_tension as Record<string, number>) || {}) : gt
        const pec = prevInd ? ((prevInd.economic_crisis as Record<string, number>) || {}) : ec
        const pdc = prevInd ? ((prevInd.doomsday_clock as Record<string, number>) || {}) : dc
        const pda = prevInd ? ((prevInd.distance_to_agi as Record<string, number>) || {}) : da
        setIndices({
          geopolitical_tension: gt.level ?? 5,
          economic_health: ec.level ?? 3,
          nuclear_danger: dc.level ?? 3,
          ai_race: da.level ?? 2,
          prev_geopolitical_tension: pgt.level ?? 5,
          prev_economic_health: pec.level ?? 3,
          prev_nuclear_danger: pdc.level ?? 3,
          prev_ai_race: pda.level ?? 2,
          outlook_tension: gt.outlook ?? 'stable',
          outlook_crisis: ec.outlook ?? 'stable',
          outlook_doom: dc.outlook ?? 'stable',
          outlook_agi: da.outlook ?? 'stable',
        })
        setColPower(ind.power_index_columbia ?? 55)
        setCatPower(ind.power_index_cathay ?? 45)
      }

      // Power history for graph — deduplicate by round (keep latest published per round)
      const { data: histRows } = await supabase
        .from('world_indicators')
        .select('round_num,power_index_columbia,power_index_cathay,created_at')
        .eq('sim_run_id', simId)
        .eq('status', 'published')
        .is('pulse_num', null)
        .order('created_at')
      if (histRows) {
        const byRound = new Map<number, {round_num: number; columbia: number; cathay: number}>()
        for (const r of histRows) {
          byRound.set(r.round_num, {
            round_num: r.round_num,
            columbia: r.power_index_columbia ?? 70,
            cathay: r.power_index_cathay ?? 50,
          })
        }
        setPowerHistory(Array.from(byRound.values()))
      }
    } catch { /* graceful */ }
  }, [simId])

  useEffect(() => { loadData() }, [loadData])

  /* News ticker page advance --------------------------------------------- */
  // SPEC_NEWS_TICKER.md §4: advance ticker page every TICKER_PAGE_DURATION_MS.
  // Mounted once; reads pageCount from a ref so timer never resets on event arrival.
  useEffect(() => {
    const TICKER_PAGE_DURATION_MS = 100_000
    const id = setInterval(() => {
      setTickerPage((prev) => {
        const pc = tickerPageCountRef.current
        return pc <= 1 ? 0 : (prev + 1) % pc
      })
    }, TICKER_PAGE_DURATION_MS)
    return () => clearInterval(id)
  }, [])

  /* Realtime subscriptions ------------------------------------------------ */
  useEffect(() => {
    if (!simId) return

    const simRunChannel = supabase
      .channel(`pub_sim:${simId}`)
      .on('postgres_changes', {
        event: 'UPDATE', schema: 'public', table: 'sim_runs',
        filter: `id=eq.${simId}`,
      }, (payload) => {
        const row = payload.new as Record<string, unknown>
        if (row) {
          setSimRun((prev) => prev ? { ...prev, ...row } as SimRun : prev)
          const newPhase = (row.current_phase as string) ?? 'pre'
          const newRound = (row.current_round as number) ?? 0
          setSimState((prev) => {
            // Refresh map when phase or round changes
            if (prev && (prev.current_phase !== newPhase || prev.current_round !== newRound)) {
              setMapKey((k) => k + 1)
            }
            return {
              status: (row.status as string) ?? 'setup',
              current_round: newRound,
              current_phase: newPhase,
              phase_started_at: (row.phase_started_at as string | null) ?? null,
              phase_duration_seconds: (row.phase_duration_seconds as number | null) ?? 3600,
              max_rounds: (row.max_rounds as number) ?? 8,
            }
          })
        }
      })
      .subscribe()

    const eventsChannel = supabase
      .channel(`pub_events:${simId}`)
      .on('postgres_changes', {
        event: 'INSERT', schema: 'public', table: 'observatory_events',
        filter: `sim_run_id=eq.${simId}`,
      }, (payload) => {
        const evt = payload.new as PublicEvent
        if (evt) {
          setEvents((prev) => [evt, ...prev].slice(0, 200))
          // Nuclear surface test alert — show full-width red banner for 30s
          if (evt.event_type === 'nuclear_test' && evt.payload?.test_type === 'surface') {
            const cc = evt.country_code ?? 'UNKNOWN'
            setNuclearAlert({ country: cc.toUpperCase(), testType: 'Surface Test' })
            if (nuclearAlertTimerRef.current) clearTimeout(nuclearAlertTimerRef.current)
            nuclearAlertTimerRef.current = setTimeout(() => setNuclearAlert(null), 30000)
          }
          // Nuclear launch resolution — dramatic banner for 60s
          if (evt.event_type === 'nuclear_launch') {
            const cc = evt.country_code ?? 'UNKNOWN'
            const hits = (evt.payload?.hits as number) ?? 0
            const launched = (evt.payload?.missiles_launched as number) ?? 0
            const targetDesc = hits > 0 ? `${hits} IMPACT${hits > 1 ? 'S' : ''}` : 'ALL INTERCEPTED'
            setNuclearLaunchAlert({ country: cc.toUpperCase(), missiles: launched, targets: targetDesc })
            if (nuclearLaunchTimerRef.current) clearTimeout(nuclearLaunchTimerRef.current)
            nuclearLaunchTimerRef.current = setTimeout(() => setNuclearLaunchAlert(null), 60000)
            // Also refresh map to show impact markers
            setMapKey((k) => k + 1)
          }
        }
      })
      .on('postgres_changes', {
        event: 'UPDATE', schema: 'public', table: 'observatory_events',
        filter: `sim_run_id=eq.${simId}`,
      }, (payload) => {
        const evt = payload.new as PublicEvent
        if (evt) {
          setEvents((prev) => {
            const exists = prev.some((e) => e.id === evt.id)
            if (exists) return prev.map((e) => e.id === evt.id ? evt : e)
            // Event made public — add it if not already in array
            return [evt, ...prev].slice(0, 200)
          })
        }
      })
      .subscribe()

    // Moderator broadcasts channel
    const broadcastCh = supabase
      .channel(`broadcasts-${simId}`)
      .on('postgres_changes', {
        event: 'INSERT', schema: 'public', table: 'public_broadcasts',
        filter: `sim_run_id=eq.${simId}`,
      }, (payload) => {
        const bc = payload.new as { id: string; content: string; created_at: string }
        setEvents((prev) => [{
          id: bc.id,
          event_type: 'moderator_broadcast',
          summary: bc.content,
          role_name: null,
          category: 'system',
          country_code: null,
          payload: null,
          created_at: bc.created_at,
          visibility: 'public',
        } as PublicEvent, ...prev].slice(0, 200))
      })
      .subscribe()

    // Nuclear actions channel — watch for flight phase (awaiting_interception)
    const nuclearChannel = supabase
      .channel(`pub_nuclear:${simId}`)
      .on('postgres_changes', {
        event: '*', schema: 'public', table: 'nuclear_actions',
        filter: `sim_run_id=eq.${simId}`,
      }, (payload) => {
        const row = payload.new as Record<string, unknown>
        if (!row) return
        if (row.status === 'awaiting_interception' && row.action_type === 'nuclear_launch') {
          const cc = (row.country_code as string) ?? 'UNKNOWN'
          const missiles = ((row.payload as Record<string,unknown>)?.changes as Record<string,unknown>)?.missiles as unknown[]
          const missileCount = missiles?.length ?? 1
          const timerStart = row.timer_started_at ? new Date(row.timer_started_at as string).getTime() : Date.now()
          const duration = (row.timer_duration_sec as number) ?? 600
          setNuclearFlightAction({ country: cc.toUpperCase(), missiles: missileCount, timerStart, duration })
        } else if (row.status === 'resolved' || row.status === 'cancelled') {
          setNuclearFlightAction(null)
          setFlightCountdown(null)
          // Refresh map to show ☢ impact markers
          setMapKey((k) => k + 1)
        }
      })
      .subscribe()

    // World indicators channel — update gauges + power graph on published assessments only
    const indicatorsChannel = supabase
      .channel(`pub_indicators:${simId}`)
      .on('postgres_changes', {
        event: '*', schema: 'public', table: 'world_indicators',
        filter: `sim_run_id=eq.${simId}`,
      }, (payload) => {
        const ind = payload.new as Record<string, unknown>
        if (!ind || ind.status !== 'published') return
        const gt = (ind.geopolitical_tension as Record<string, string | number>) || {}
        const ec = (ind.economic_crisis as Record<string, string | number>) || {}
        const dc = (ind.doomsday_clock as Record<string, string | number>) || {}
        const da = (ind.distance_to_agi as Record<string, string | number>) || {}
        setIndices(prev => ({
          geopolitical_tension: (gt.level as number) ?? prev.geopolitical_tension,
          economic_health: (ec.level as number) ?? prev.economic_health,
          nuclear_danger: (dc.level as number) ?? prev.nuclear_danger,
          ai_race: (da.level as number) ?? prev.ai_race,
          prev_geopolitical_tension: prev.geopolitical_tension,
          prev_economic_health: prev.economic_health,
          prev_nuclear_danger: prev.nuclear_danger,
          prev_ai_race: prev.ai_race,
          outlook_tension: (gt.outlook as string) ?? prev.outlook_tension,
          outlook_crisis: (ec.outlook as string) ?? prev.outlook_crisis,
          outlook_doom: (dc.outlook as string) ?? prev.outlook_doom,
          outlook_agi: (da.outlook as string) ?? prev.outlook_agi,
        }))
        setColPower((ind.power_index_columbia as number) ?? 55)
        setCatPower((ind.power_index_cathay as number) ?? 45)
        // Update history if it's a Phase B update (no pulse_num).
        // IMPORTANT: replace any existing entry for this round_num — multiple
        // published rows can exist per round (auto-publish on round advance,
        // LLM re-generation, manual moderator publish). Earlier code just
        // appended, so PowerTrendGraph's `find` would return the OLDEST
        // entry and never reflect updates — graph appeared frozen (§1.6).
        if (!ind.pulse_num) {
          setPowerHistory(prev => {
            const round = (ind.round_num as number) ?? 0
            const next = prev.filter(p => p.round_num !== round)
            next.push({
              round_num: round,
              columbia: (ind.power_index_columbia as number) ?? 70,
              cathay: (ind.power_index_cathay as number) ?? 50,
            })
            return next.sort((a, b) => a.round_num - b.round_num)
          })
        }
      })
      .subscribe()

    const fallback = setInterval(loadData, 10000)

    return () => {
      supabase.removeChannel(simRunChannel)
      supabase.removeChannel(eventsChannel)
      supabase.removeChannel(broadcastCh)
      supabase.removeChannel(nuclearChannel)
      supabase.removeChannel(indicatorsChannel)
      clearInterval(fallback)
    }
  }, [simId, loadData])

  /* Timer countdown ------------------------------------------------------- */
  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current)
    if (!simState?.phase_started_at || !simState?.phase_duration_seconds || ['paused','setup','pre_start','completed','processing'].includes(simState.status || '')) {
      setRemaining(null)
      return
    }
    const tick = () => {
      const now = Date.now()
      const started = new Date(simState.phase_started_at!).getTime()
      const elapsed = (now - started) / 1000
      setRemaining((simState.phase_duration_seconds ?? 3600) - elapsed)
    }
    tick()
    timerRef.current = setInterval(tick, 1000)
    return () => { if (timerRef.current) clearInterval(timerRef.current) }
  }, [simState?.phase_started_at, simState?.phase_duration_seconds, simState?.status])

  /* Nuclear flight countdown ----------------------------------------------- */
  useEffect(() => {
    if (!nuclearFlightAction) { setFlightCountdown(null); return }
    const tick = () => {
      const elapsed = (Date.now() - nuclearFlightAction.timerStart) / 1000
      const remaining = Math.max(0, nuclearFlightAction.duration - elapsed)
      setFlightCountdown(remaining)
    }
    tick()
    const interval = setInterval(tick, 1000)
    return () => clearInterval(interval)
  }, [nuclearFlightAction])

  /* Screen A/B rotation --------------------------------------------------- */
  useEffect(() => {
    if (screenMode === 'rotate') {
      const interval = setInterval(() => {
        setActiveScreen(prev => prev === 'a' ? 'b' : 'a')
      }, 15000)
      return () => clearInterval(interval)
    }
    setActiveScreen(screenMode === 'b' ? 'b' : 'a')
  }, [screenMode])

  // Read screen_b_frozen from sim_runs
  useEffect(() => {
    if (!simId) return
    const checkFrozen = async () => {
      const { data } = await supabase.from('sim_runs').select('screen_b_frozen').eq('id', simId).single()
      if (data) setScreenBFrozen(data.screen_b_frozen ?? false)
    }
    checkFrozen()
    const ch = supabase.channel(`screen-b-frozen-${simId}`)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'sim_runs', filter: `id=eq.${simId}` }, () => checkFrozen())
      .subscribe()
    return () => { supabase.removeChannel(ch) }
  }, [simId])

  // Auto-hide toggle after 5s of inactivity
  useEffect(() => {
    const handleMove = () => {
      setShowToggle(true)
      if (toggleTimerRef.current) clearTimeout(toggleTimerRef.current)
      toggleTimerRef.current = setTimeout(() => setShowToggle(false), 5000)
    }
    window.addEventListener('mousemove', handleMove)
    toggleTimerRef.current = setTimeout(() => setShowToggle(false), 5000)
    return () => {
      window.removeEventListener('mousemove', handleMove)
      if (toggleTimerRef.current) clearTimeout(toggleTimerRef.current)
    }
  }, [])

  /* Derived --------------------------------------------------------------- */
  const currentRound = simState?.current_round ?? 0
  const currentPhase = simState?.current_phase ?? 'pre'
  const simDate = ROUND_TO_DATE[currentRound] ?? `R${currentRound}`
  const NEWS_TTL_MS = 10 * 60 * 1000 // 10 minutes
  const now = Date.now()
  // Block-list applied even to events with visibility='public'. Two reasons
  // an event lands here:
  //   (a) Low signal — engine emits visibility='public' but the summary
  //       carries no useful content (e.g. "Tariffs decision submitted"
  //       without the imposer/target/level). Until the engine emits a
  //       meaningful summary, the row is suppressed.
  //   (b) Visibility mistake — the event type is conceptually country-private
  //       (troop movement, internal reorg) but the engine occasionally
  //       emits it as public. Filter at the frontend until the engine fix
  //       lands.
  // See SPEC_NEWS_TICKER.md §3.6 for per-entry justification.
  const LOW_SIGNAL_PUBLIC_TYPES = new Set([
    'set_tariffs',       // (a) summary lacks parameters
    'set_sanctions',     // (a) summary lacks parameters
    'ground_move',       // (b) movement is country-private; only combat results should surface
  ])
  const significantEvents = events
    .filter((e) => {
      // TTL: news expires 10 min after published_at (moderator PUB) or created_at
      const timestamp = (e as Record<string,unknown>).published_at as string || e.created_at
      if (now - new Date(timestamp).getTime() > NEWS_TTL_MS) return false
      // Drop events whose public summary doesn't actually carry content
      if (LOW_SIGNAL_PUBLIC_TYPES.has(e.event_type)) return false
      // Nuclear tests: BOTH surface and underground tests appear in the ticker.
      // Surface tests carry the attribution as-is (everyone sees the flash).
      // Underground tests are anonymised at render time below — the world knows
      // a test happened (seismic detection) but not who or where. See
      // SPEC_NEWS_TICKER.md §3.6 for the inclusion-rules table.
      // Use visibility column if present; fall back to hardcoded set for old events
      if (e.visibility != null) {
        if (e.visibility !== 'public') return false
      } else {
        if (!PUBLIC_NEWS_EVENTS.has(e.event_type)) return false
      }
      // Agreements: exclude secret agreements (safety check)
      if (e.event_type === 'agreement_activated') {
        return e.payload?.visibility !== 'secret'
      }
      return true
    })
    .map((e) => {
      // Underground nuclear tests are anonymised before they hit the ticker.
      // The country/role names baked into the summary are replaced with neutral
      // wording so the bulletin only conveys "someone, somewhere, conducted an
      // underground test" — consistent with how seismic detection actually works.
      if (e.event_type === 'nuclear_test' && e.payload?.test_type === 'underground') {
        const success = /SUCCESS/i.test(e.summary || '')
        const failure = /FAIL/i.test(e.summary || '')
        const outcome = success ? 'success suspected' : failure ? 'apparent failure' : 'outcome unclear'
        // Strip role/country attribution so even the role-name column is empty.
        return {
          ...e,
          summary: `Seismic event consistent with an underground nuclear test detected — ${outcome}`,
          role_name: null,
          country_code: null,
        }
      }
      return e
    })
    // FIFO — pure recency, newest first. Category weight intentionally NOT
    // applied: with a 20-event cap and 10-min TTL the natural mix is already
    // representative, and category-weighting was pushing dramatic-but-old
    // military events ahead of fresh public statements / agreements.
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())

  // News ticker — hard cap at TICKER_MAX events (top 20 by category weight,
  // then recency). Earlier we had multi-page cycling, but with the two-track
  // marquee + alternating row directions the screen visually overlapped when
  // a page had many short items. A flat cap at 20 keeps the ticker dense
  // enough that the loop boundary is invisible but not so dense that items
  // physically collide on screen. Older events drop out by TTL (10 min) and
  // any beyond the top-20 are dropped here.
  const TICKER_MAX = 15
  const tickerPageEvents = significantEvents.slice(0, TICKER_MAX)
  // Pagination ref kept at 1 so the page-advance interval (mounted once at
  // top of the file) is a no-op.
  tickerPageCountRef.current = 1
  const tickerSafePage = 0
  // Pre-split into 3 rows for the ticker — events are interleaved across rows
  // by index `i % 3`. Each row is a single animated track with padding-left:
  // 100% — content enters from the entering edge, traverses, exits, brief
  // empty moment at the loop boundary, repeats. See SPEC_NEWS_TICKER.md §3.4.
  const tickerLine1 = tickerPageEvents.filter((_, i) => i % 3 === 0)
  const tickerLine2 = tickerPageEvents.filter((_, i) => i % 3 === 1)
  const tickerLine3 = tickerPageEvents.filter((_, i) => i % 3 === 2)

  // Detect active combat in each theater (current round events)
  const COMBAT_TYPES = new Set(['ground_attack', 'air_strike', 'naval_combat', 'naval_bombardment', 'naval_blockade', 'launch_missile_conventional'])
  const combatEvents = events.filter((e) => COMBAT_TYPES.has(e.event_type))
  const hasEasternEreb = combatEvents.some((e) => e.payload?.theater === 'eastern_ereb')
  const hasMashriq = combatEvents.some((e) => e.payload?.theater === 'mashriq')

  // Build rotation panels: always world status, + active theaters
  const sidebarPanels: string[] = ['world_status']
  if (hasEasternEreb) sidebarPanels.push('eastern_ereb')
  if (hasMashriq) sidebarPanels.push('mashriq')

  // Rotate every 15 seconds
  const [sidebarIndex, setSidebarIndex] = useState(0)
  useEffect(() => {
    if (sidebarPanels.length <= 1) return
    const interval = setInterval(() => {
      setSidebarIndex((prev) => (prev + 1) % sidebarPanels.length)
    }, 15000)
    return () => clearInterval(interval)
  }, [sidebarPanels.length])

  const activeSidebarPanel = sidebarPanels[sidebarIndex % sidebarPanels.length] ?? 'world_status'

  /* Render ---------------------------------------------------------------- */
  if (!simRun) {
    return (
      <div className="min-h-screen bg-[#0A0E1A] flex items-center justify-center">
        <p className="font-heading text-h2 text-text-secondary">Loading simulation...</p>
      </div>
    )
  }

  return (
    <div className="h-screen bg-[#0A0E1A] flex flex-col overflow-hidden">
      {/* ================================================================ */}
      {/*  HEADER                                                          */}
      {/* ================================================================ */}
      <header className="flex items-center justify-between px-6 py-3 border-b border-white/10">
        <div className="flex items-center gap-6">
          <span className="font-heading text-3xl text-white tracking-wide">
            R{currentRound}
          </span>
          {currentPhase === 'B' && (
            <span className="font-body text-lg text-warning uppercase tracking-widest">
              PROCESSING
            </span>
          )}
          {simState?.status === 'paused' && (
            <span className="font-body text-lg text-text-secondary uppercase tracking-widest">
              PAUSED
            </span>
          )}
          {remaining !== null && (
            <span className={`font-data text-3xl ${
              remaining < 0 ? 'text-danger animate-pulse' : 'text-white'
            }`}>
              {formatTimer(remaining)}
            </span>
          )}
        </div>
        <div className="flex items-center gap-6">
          {/* Layout / zoom switcher — varies by which screen is active.
              For the big-screen setup in the game room: pick a denser layout
              when the projector throws content too small. */}
          {activeScreen === 'a' ? (
            <div className="flex items-center gap-0.5 bg-black/40 border border-white/10 rounded-full px-1 py-0.5">
              {([
                ['full', 'Full'],
                ['map-ticker', 'Map+'],
                ['map', 'Map'],
              ] as const).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setLayoutA(key)}
                  className={`font-data text-[11px] px-2.5 py-1 rounded-full transition-colors ${
                    layoutA === key ? 'bg-white/15 text-white' : 'text-white/40 hover:text-white/70'
                  }`}
                  title={key === 'full' ? 'Map + gauges + ticker'
                    : key === 'map-ticker' ? 'Map + ticker (hide right panel)'
                    : 'Map only (full screen)'}
                >
                  {label}
                </button>
              ))}
            </div>
          ) : (
            <div className="flex items-center gap-0.5 bg-black/40 border border-white/10 rounded-full px-1 py-0.5">
              {([
                ['full', 'Full'],
                ['stats', 'Stats'],
                ['events', 'Events'],
              ] as const).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setLayoutB(key)}
                  className={`font-data text-[11px] px-2.5 py-1 rounded-full transition-colors ${
                    layoutB === key ? 'bg-white/15 text-white' : 'text-white/40 hover:text-white/70'
                  }`}
                  title={key === 'full' ? 'Stats + events grid'
                    : key === 'stats' ? 'Country rankings only'
                    : 'Events grid only'}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
          <span className="font-heading text-xl text-accent">
            {simDate}
          </span>
          <span className="font-body text-sm text-white/40">
            {simRun.name}
          </span>
        </div>
      </header>

      {/* ── Nuclear Alert Banner ── */}
      {nuclearAlert && (
        <div style={{
          width: '100%',
          backgroundColor: '#991B1B',
          padding: '0.75rem 1.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '1rem',
          animation: 'nuclearBannerFlash 2s ease-in-out infinite',
          zIndex: 40,
        }}>
          <span style={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '1.25rem',
            fontWeight: 700,
            color: '#FFFFFF',
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
          }}>
            {'\u26A0'} NUCLEAR TEST DETECTED {'\u2014'} {nuclearAlert.country} {'\u2014'} {nuclearAlert.testType}
          </span>
        </div>
      )}
      {/* ── Nuclear Flight Banner (Phase 3 — missiles in flight) ── */}
      {nuclearFlightAction && (
        <div style={{
          width: '100%',
          backgroundColor: '#7F1D1D',
          padding: '1rem 1.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '1.5rem',
          animation: 'nuclearBannerFlash 1.5s ease-in-out infinite',
          zIndex: 40,
        }}>
          <span style={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '1.5rem',
            fontWeight: 700,
            color: '#FFFFFF',
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
          }}>
            {'\u26A0'} BALLISTIC MISSILE LAUNCH DETECTED {'\u2014'} {nuclearFlightAction.country}
          </span>
          <span style={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '1.5rem',
            fontWeight: 700,
            color: '#FFFFFF',
            letterSpacing: '0.1em',
          }}>
            {nuclearFlightAction.missiles} MISSILE{nuclearFlightAction.missiles > 1 ? 'S' : ''} INBOUND
            {flightCountdown !== null && ` \u2014 Impact in ${formatTimer(flightCountdown)}`}
          </span>
        </div>
      )}

      {/* ── Nuclear Launch Resolution Banner (60s) ── */}
      {nuclearLaunchAlert && !nuclearFlightAction && (
        <div style={{
          width: '100%',
          backgroundColor: '#7F1D1D',
          padding: '1rem 1.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '1.5rem',
          zIndex: 40,
        }}>
          <span style={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '1.5rem',
            fontWeight: 700,
            color: '#FFFFFF',
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
          }}>
            {'\u2622'} NUCLEAR STRIKE {'\u2014'} {nuclearLaunchAlert.country} {'\u2014'} {nuclearLaunchAlert.targets}
          </span>
        </div>
      )}

      <style>{`
        @keyframes nuclearBannerFlash {
          0%, 100% { background-color: #991B1B; }
          50% { background-color: #DC2626; }
        }
      `}</style>

      {/* ================================================================ */}
      {/*  SCREEN A/B CONTENT                                              */}
      {/* ================================================================ */}

      {/* Screen B: Numbers + Events */}
      {activeScreen === 'b' && simId && (
        <div className="flex-1 min-h-0 relative">
          <ScreenB simId={simId} currentRound={currentRound} frozen={screenBFrozen} layoutMode={layoutB} />
        </div>
      )}

      {/* Screen A: Map + Gauges + Ticker (existing) */}
      {activeScreen === 'a' && <>
      <div className="flex-1 flex overflow-hidden">

        {/* ── LEFT: MAP + COLUMBIA-CATHAY (~65% in 'full', 100% in 'map-ticker'/'map') */}
        <div className={`${layoutA === 'full' ? 'flex-[2] border-r border-white/10' : 'flex-1'} flex flex-col`}>
          {/* Map iframe + theater switcher overlay (bottom-left) */}
          <div className="flex-1 relative">
            <iframe
              key={`map-${mapKey}-${mapTheater}`}
              src={`/map/deployments.html?display=clean&sim_run_id=${simId}${mapTheater !== 'global' ? `&theater=${mapTheater}` : ''}`}
              className="absolute inset-0 w-full h-full border-0"
              title={mapTheater === 'global' ? 'Global Map' : mapTheater === 'eastern_ereb' ? 'Eastern Ereb Theater' : 'Mashriq Theater'}
            />
            {/* Theater switcher — bottom-left overlay, 3 buttons */}
            <div className="absolute bottom-4 left-4 z-10 flex gap-1 bg-black/60 backdrop-blur-sm border border-white/10 rounded-lg p-1 shadow-xl">
              {(['global', 'eastern_ereb', 'mashriq'] as const).map(t => {
                const label = t === 'global' ? 'Global' : t === 'eastern_ereb' ? 'E. Ereb' : 'Mashriq'
                const isActive = mapTheater === t
                return (
                  <button
                    key={t}
                    onClick={() => setMapTheater(t)}
                    className={`font-body text-sm px-3 py-1.5 rounded transition-colors ${
                      isActive
                        ? 'bg-action text-white font-semibold'
                        : 'text-white/60 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    {label}
                  </button>
                )
              })}
            </div>
          </div>

        </div>

        {/* ── RIGHT PANEL (~35%) — rotates between world status + theater maps.
             Hidden when layoutA is 'map-ticker' or 'map' so the map can fill the row. */}
        {layoutA === 'full' && (
        <div className="flex-[1] flex flex-col min-w-[280px] relative">

          {/* Rotation indicator (dots) */}
          {sidebarPanels.length > 1 && (
            <div className="absolute top-2 right-3 flex gap-1 z-10">
              {sidebarPanels.map((panel, i) => (
                <span
                  key={panel}
                  className={`w-2 h-2 rounded-full transition-all ${
                    i === sidebarIndex % sidebarPanels.length
                      ? 'bg-white/60 scale-125'
                      : 'bg-white/15'
                  }`}
                />
              ))}
            </div>
          )}

          {/* Panel: World Status */}
          {activeSidebarPanel === 'world_status' && (
            <div className="flex flex-col animate-[fadeIn_0.5s_ease]">
              <div className="px-5 py-3 space-y-2">
                <h3 className="font-heading text-xs text-white/40 uppercase tracking-widest">
                  World Status
                </h3>
                <DoomsdayGauge
                  label="Geopolitical Tension"
                  value={indices.geopolitical_tension}
                  prev={indices.prev_geopolitical_tension}
                  max={10} dangerUp colorHigh="danger"
                  outlook={indices.outlook_tension}
                />
                <DoomsdayGauge
                  label="Economic Stability"
                  value={indices.economic_health}
                  prev={indices.prev_economic_health}
                  max={10} dangerUp colorHigh="danger"
                  outlook={indices.outlook_crisis}
                />
                <DoomsdayGauge
                  label="Doomsday Clock"
                  value={indices.nuclear_danger}
                  prev={indices.prev_nuclear_danger}
                  max={10} dangerUp colorHigh="danger"
                  outlook={indices.outlook_doom}
                />
                <DoomsdayGauge
                  label="AGI Proximity"
                  value={indices.ai_race}
                  prev={indices.prev_ai_race}
                  max={10} dangerUp colorHigh="warning"
                  outlook={indices.outlook_agi}
                />
              </div>
            </div>
          )}

          {/* Panel: Eastern Ereb theater */}
          {activeSidebarPanel === 'eastern_ereb' && (
            <div className="flex-1 flex flex-col animate-[fadeIn_0.5s_ease]">
              <div className="px-5 py-3 border-b border-white/10">
                <h3 className="font-heading text-sm text-danger uppercase tracking-widest">
                  Eastern Ereb — Active Combat
                </h3>
              </div>
              <div className="flex-1 relative">
                <iframe
                  key={`ee-${mapKey}`}
                  src={`/map/deployments.html?display=clean&theater=eastern_ereb&sim_run_id=${simId}`}
                  className="absolute inset-0 w-full h-full border-0"
                  title="Eastern Ereb Theater"
                />
              </div>
            </div>
          )}

          {/* Panel: Mashriq theater */}
          {activeSidebarPanel === 'mashriq' && (
            <div className="flex-1 flex flex-col animate-[fadeIn_0.5s_ease]">
              <div className="px-5 py-3 border-b border-white/10">
                <h3 className="font-heading text-sm text-danger uppercase tracking-widest">
                  Mashriq — Active Combat
                </h3>
              </div>
              <div className="flex-1 relative">
                <iframe
                  key={`mq-${mapKey}`}
                  src={`/map/deployments.html?display=clean&theater=mashriq&sim_run_id=${simId}`}
                  className="absolute inset-0 w-full h-full border-0"
                  title="Mashriq Theater"
                />
              </div>
            </div>
          )}

          {/* Global Power Index — always visible, pinned at bottom */}
          <div className="px-5 py-3 border-t border-white/10">
            <h3 className="font-heading text-xs text-white/40 uppercase tracking-widest mb-2">
              Global Power Index — R{currentRound}
            </h3>
            <PowerTrendGraph colPower={colPower} catPower={catPower} currentRound={currentRound} powerHistory={powerHistory} />
          </div>
        </div>
        )}
      </div>

      {/* ================================================================ */}
      {/*  NEWS TICKER (bottom) — hidden when layoutA === 'map' so the map  */}
      {/*  can use the full viewport vertical space.                       */}
      {/* ================================================================ */}
      {layoutA !== 'map' && (
      <footer className="border-t border-white/10 bg-[#0D1220] overflow-hidden" style={{ height: '120px' }}>
        {significantEvents.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <span className="font-body text-sm text-white/30">Awaiting events...</span>
          </div>
        ) : (
          <>
            {/* Three rows of marquee, single track per row. The previous two-track
                half-cycle-offset design was meant to fill the empty gap at the loop
                boundary, but with the 15-event cap split 3 ways (~5 items per row) the
                two overlapping tracks collided visually on screen. Reverted to single
                track — accept the brief empty moment at the loop boundary. Directions
                alternate: line 1 → right-to-left, line 2 → left-to-right (reverse),
                line 3 → right-to-left. See SPEC_NEWS_TICKER.md §3.4. */}
            {/* Line 1 — fastest (80s), right-to-left */}
            <div className="overflow-hidden whitespace-nowrap px-6 py-2 border-b border-white/5 last:border-b-0">
              <div
                key={`ticker-row1-p${tickerSafePage}`}
                className="ticker-track inline-flex items-center gap-8"
                style={{ animation: 'tickerMarquee 80s linear infinite' }}
              >
                {tickerLine1.map((evt) => (
                  <span key={evt.id} className="font-body text-sm text-white/70 inline-flex items-center gap-2">
                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                      evt.category === 'military' ? 'bg-danger' :
                      evt.category === 'economic' ? 'bg-accent' :
                      evt.category === 'diplomatic' ? 'bg-action' :
                      evt.category === 'political' ? 'bg-warning' : 'bg-white/30'
                    }`} />
                    {evt.summary}
                  </span>
                ))}
              </div>
            </div>
            {/* Line 2 — medium (90s), left-to-right (reverse) for visual rhythm */}
            <div className="overflow-hidden whitespace-nowrap px-6 py-2 border-b border-white/5 last:border-b-0">
              <div
                key={`ticker-row2-p${tickerSafePage}`}
                className="ticker-track inline-flex items-center gap-8"
                style={{ animation: 'tickerMarquee 90s linear infinite reverse' }}
              >
                {tickerLine2.map((evt) => (
                  <span key={evt.id} className="font-body text-sm text-white/60 inline-flex items-center gap-2">
                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                      evt.category === 'military' ? 'bg-danger' :
                      evt.category === 'economic' ? 'bg-accent' :
                      evt.category === 'diplomatic' ? 'bg-action' :
                      evt.category === 'political' ? 'bg-warning' : 'bg-white/30'
                    }`} />
                    {evt.summary}
                  </span>
                ))}
              </div>
            </div>
            {/* Line 3 — slowest (100s), right-to-left */}
            <div className="overflow-hidden whitespace-nowrap px-6 py-2 border-b border-white/5 last:border-b-0">
              <div
                key={`ticker-row3-p${tickerSafePage}`}
                className="ticker-track inline-flex items-center gap-8"
                style={{ animation: 'tickerMarquee 100s linear infinite' }}
              >
                {tickerLine3.map((evt) => (
                  <span key={evt.id} className="font-body text-sm text-white/50 inline-flex items-center gap-2">
                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                      evt.category === 'military' ? 'bg-danger' :
                      evt.category === 'economic' ? 'bg-accent' :
                      evt.category === 'diplomatic' ? 'bg-action' :
                      evt.category === 'political' ? 'bg-warning' : 'bg-white/30'
                    }`} />
                    {evt.summary}
                  </span>
                ))}
              </div>
            </div>
          </>
        )}
      </footer>
      )}

      </>}

      {/* ── A/B Toggle (bottom-right, fades on inactivity) ─────────────── */}
      <div
        className={`fixed bottom-4 right-4 z-50 flex items-center gap-0.5 bg-black/60 backdrop-blur-sm border border-white/10 rounded-full px-1 py-0.5 transition-opacity duration-500 ${showToggle ? 'opacity-100' : 'opacity-0'}`}
        onMouseEnter={() => setShowToggle(true)}
      >
        {(['a', 'b', 'rotate'] as const).map(mode => (
          <button
            key={mode}
            onClick={() => setScreenMode(mode)}
            className={`font-data text-[11px] px-2.5 py-1 rounded-full transition-colors ${
              screenMode === mode ? 'bg-white/15 text-white' : 'text-white/40 hover:text-white/70'
            }`}
          >
            {mode === 'rotate' ? '⟳ 15s' : mode.toUpperCase()}
          </button>
        ))}
      </div>

      {/* Ticker animation — padding-left: 100% pattern so content always enters from the
          right edge of the row, regardless of how many items the row has. The track is
          translated by -100% of its own width per cycle, carrying all content off the left
          edge. On loop, the animation snaps back to the start state in one frame, which
          visually reads as "the same item starts coming from the right again". See
          SPEC_NEWS_TICKER.md §3.4. */}
      <style>{`
        @keyframes tickerMarquee {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-100%); }
        }
        .ticker-track {
          padding-left: 100%;
          will-change: transform;
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/*  Sub-components                                                            */
/* -------------------------------------------------------------------------- */

/** Doomsday gauge — analogue speedometer style with needle. */
function DoomsdayGauge({
  label, value, prev, min = 1, mid = 5, max, dangerUp, colorHigh, outlook,
}: {
  label: string
  value: number
  prev: number
  min?: number
  mid?: number
  max: number
  dangerUp: boolean
  colorHigh: 'danger' | 'success' | 'warning' | 'accent'
  outlook?: string
}) {
  // World Indicators use a 1..10 scale where the integer 5 is the conceptual
  // midpoint (below 5 = bad, above 5 = good or vice versa per dangerUp). The
  // strict linear mapping (value-min)/(max-min) puts value=5 at ~44 % because
  // the arithmetic midpoint is 5.5 — visually too far left. Use a piecewise
  // linear interpolation anchored on three points: min -> 0, mid -> 0.5,
  // max -> 1. The asymmetry below vs above mid (4 units vs 5 units on the
  // 1..10 scale) is absorbed into the per-half slope.
  let pct: number
  if (value <= mid) {
    pct = (value - min) / Math.max(1, mid - min) * 0.5
  } else {
    pct = 0.5 + (value - mid) / Math.max(1, max - mid) * 0.5
  }
  pct = Math.min(1, Math.max(0, pct))
  // Use actual outlook from DB, not calculated from level diff
  const outlookLabel = outlook === 'improving' ? 'improving' : outlook === 'deteriorating' ? 'deteriorating' : 'stable'
  const arrow = outlook === 'improving' ? '\u2193' : outlook === 'deteriorating' ? '\u2191' : '\u2192'
  const trend = outlook === 'deteriorating' ? 'text-danger' : outlook === 'improving' ? 'text-success' : 'text-text-secondary'

  // Needle sweeps a 180-degree arc matching the painted semicircle:
  //   value=0     -> pi rad   -> points LEFT  (arc's left end)
  //   value=mid   -> pi/2 rad -> points UP    (arc's top)
  //   value=max   -> 0 rad    -> points RIGHT (arc's right end)
  // SVG y axis is inverted, so subtract sin(theta)*r from cy to go upward.
  const needleAngleRad = Math.PI * (1 - pct)
  const needleX = 50 + 30 * Math.cos(needleAngleRad)
  const needleY = 55 - 30 * Math.sin(needleAngleRad)

  // Arc color gradient stops
  const arcColor = colorHigh === 'danger' ? '#EF4444'
    : colorHigh === 'success' ? '#22C55E'
    : colorHigh === 'warning' ? '#F59E0B'
    : '#3B82F6'

  return (
    <div className="flex items-center gap-3">
      {/* Speedometer SVG with color zones */}
      <svg viewBox="0 0 100 60" className="w-20 h-12 shrink-0">
        {/* Color zone arcs — 4 zones, each ~31.5px of ~126px total arc */}
        {/* dangerUp: green→yellow→red→deepred (high=bad) */}
        {/* !dangerUp: deepred→red→yellow→green (high=good, e.g. Economic Health) */}
        {(dangerUp
          ? ['#22C55E', '#F59E0B', '#EF4444', '#991B1B']
          : ['#991B1B', '#EF4444', '#F59E0B', '#22C55E']
        ).map((color, i) => (
          <path key={i} d="M 10 55 A 40 40 0 0 1 90 55" fill="none"
            stroke={color} strokeWidth="6" strokeLinecap="butt"
            strokeDasharray="31.5 94.5" strokeDashoffset={-i * 31.5}
            opacity={i === 3 ? 0.55 : 0.45} />
        ))}
        {/* Needle */}
        <line
          x1="50" y1="55"
          x2={needleX}
          y2={needleY}
          stroke="white"
          strokeWidth="2.5"
          strokeLinecap="round"
          opacity="0.95"
        />
        {/* Center dot */}
        <circle cx="50" cy="55" r="3" fill="white" opacity="0.95" />
        {/* No number — needle position is the indicator */}
      </svg>

      {/* Label + trend */}
      <div className="flex-1 min-w-0">
        <div className="font-body text-xs text-white/60 uppercase tracking-wider truncate">
          {label}
        </div>
        <div className="flex items-center gap-1 mt-0.5">
          <span className={`font-data text-xs ${trend}`}>
            {arrow} {outlookLabel}
          </span>
        </div>
      </div>
    </div>
  )
}

/** Columbia vs Cathay power balance — live bar. */
function PowerBalanceBar({ simId }: { simId: string }) {
  const [col, setCol] = useState(55)
  const [cat, setCat] = useState(45)

  useEffect(() => {
    if (!simId) return
    Promise.all([
      supabase.from('countries').select('id, gdp, nuclear_level, ai_level').eq('sim_run_id', simId).in('id', ['columbia', 'cathay']),
      supabase.from('deployments').select('country_code, unit_type').eq('sim_run_id', simId).neq('unit_status', 'destroyed').in('country_code', ['columbia', 'cathay']),
    ]).then(([cRes, dRes]) => {
        const data = cRes.data
        const deps = dRes.data ?? []
        if (!data || data.length < 2) return
        // Inject deployment counts
        const mc: Record<string,{mil_ground:number;mil_naval:number;mil_tactical_air:number;mil_strategic_missiles:number;mil_air_defense:number}> = {}
        for(const d of deps){
          if(!mc[d.country_code]) mc[d.country_code]={mil_ground:0,mil_naval:0,mil_tactical_air:0,mil_strategic_missiles:0,mil_air_defense:0}
          if(d.unit_type==='ground') mc[d.country_code].mil_ground++
          else if(d.unit_type==='naval') mc[d.country_code].mil_naval++
          else if(d.unit_type==='tactical_air') mc[d.country_code].mil_tactical_air++
          else if(d.unit_type==='strategic_missile') mc[d.country_code].mil_strategic_missiles++
          else if(d.unit_type==='air_defense') mc[d.country_code].mil_air_defense++
        }
        for(const c of data){ const m=mc[c.id]; if(m) Object.assign(c,m); else { (c as any).mil_ground=0;(c as any).mil_naval=0;(c as any).mil_tactical_air=0;(c as any).mil_strategic_missiles=0;(c as any).mil_air_defense=0 } }
        const colD = data.find((c) => c.id === 'columbia')
        const catD = data.find((c) => c.id === 'cathay')
        if (!colD || !catD) return

        const milScore = (c: any) =>
          (c.mil_ground ?? 0) + (c.mil_naval ?? 0) * 2 + (c.mil_tactical_air ?? 0) * 1.5 +
          (c.mil_strategic_missiles ?? 0) * 3 + (c.mil_air_defense ?? 0)

        const power = (c: any) =>
          (c.gdp ?? 0) * 0.4 + milScore(c) * 0.3 + (c.nuclear_level ?? 0) * 10 + (c.ai_level ?? 0) * 8

        const cp = power(colD)
        const tp = power(catD)
        const total = cp + tp || 1
        setCol(Math.round((cp / total) * 100))
        setCat(Math.round((tp / total) * 100))
      })
  }, [simId])

  return (
    <div className="flex items-center gap-3">
      <span className="font-data text-xs w-24 text-right" style={{ color: '#3A6B9F' }}>
        Columbia {col}%
      </span>
      <div className="flex-1 flex h-4 rounded-full overflow-hidden bg-white/5">
        <div className="transition-all duration-1000" style={{ width: `${col}%`, backgroundColor: 'rgba(58,107,159,0.7)' }} />
        <div className="transition-all duration-1000" style={{ width: `${cat}%`, backgroundColor: 'rgba(196,160,48,0.7)' }} />
      </div>
      <span className="font-data text-xs w-24" style={{ color: '#C4A030' }}>
        {cat}% Cathay
      </span>
    </div>
  )
}

/** Columbia vs Cathay historical power trend — two lines converging. */
function PowerTrendGraph({ colPower, catPower, currentRound, powerHistory }: {
  colPower: number; catPower: number; currentRound: number;
  powerHistory: {round_num: number; columbia: number; cathay: number}[]
}) {
  // Historical: 2006-2026 (expert-estimated, fixed)
  // Scale: 0-100 composite power index
  // Columbia: slow steady growth (established power, mature economy)
  // Cathay: rapid catch-up (rising power, fast industrialization + tech)
  const historicalLabels = ['06','08','10','12','14','16','18','20','22','24','26']
  const historicalCol =    [78,  79,  80,  81,  82,  83,  83,  84,  84,  85,  85]
  const historicalCat =    [35,  39,  43,  48,  52,  56,  60,  64,  67,  70,  72]

  // SIM rounds: use real data from world_indicators table
  const maxRounds = 8
  const simLabels = Array.from({ length: maxRounds }, (_, i) => `R${i + 1}`)

  // Build SIM data arrays from history, fill with latest values for current round
  const simCol: (number | null)[] = simLabels.map((_, i) => {
    const roundData = powerHistory.find(h => h.round_num === i + 1)
    if (roundData) return roundData.columbia
    if (i + 1 === currentRound) return colPower
    return null // future rounds — no data yet
  })
  const simCat: (number | null)[] = simLabels.map((_, i) => {
    const roundData = powerHistory.find(h => h.round_num === i + 1)
    if (roundData) return roundData.cathay
    if (i + 1 === currentRound) return catPower
    return null
  })

  const allLabels = [...historicalLabels, ...simLabels]
  // Combine historical + SIM data (filter nulls for line drawing)
  const allColRaw = [...historicalCol, ...simCol]
  const allCatRaw = [...historicalCat, ...simCat]

  const maxVal = 105 // fixed scale 0-100 with margin
  const w = 360
  const h = 144
  const n = allLabels.length

  const xForIdx = (i: number) => i * (w / (n - 1))
  const yForVal = (v: number) => h - (v / maxVal) * h

  // Build polyline points, stopping at last non-null value
  const toPoints = (data: (number | null)[]) => {
    const pts: string[] = []
    for (let i = 0; i < data.length; i++) {
      if (data[i] === null) break
      pts.push(`${xForIdx(i)},${yForVal(data[i]!)}`)
    }
    return pts.join(' ')
  }

  const simStartX = xForIdx(historicalLabels.length - 1)
  const lastColIdx = allColRaw.findLastIndex(v => v !== null)
  const lastCatIdx = allCatRaw.findLastIndex(v => v !== null)

  // Dangerous Proximity zone: where the gap between powers is < 15 points
  const proximityPaths: string[] = []
  for (let i = historicalLabels.length; i < allColRaw.length; i++) {
    const c = allColRaw[i]; const t = allCatRaw[i]
    if (c === null || t === null) break
    if (Math.abs(c - t) < 15) {
      const x = xForIdx(i)
      const y1 = yForVal(Math.max(c, t))
      const y2 = yForVal(Math.min(c, t))
      proximityPaths.push(`<rect x="${x - 5}" y="${y1}" width="10" height="${y2 - y1}" />`)
    }
  }

  return (
    <div>
      <svg viewBox={`0 -16 ${w} ${h + 34}`} className="w-full h-40">
        {/* Grid lines */}
        {[0.25, 0.5, 0.75].map((f) => (
          <line key={f} x1="0" y1={h * f} x2={w} y2={h * f} stroke="white" strokeWidth="0.3" opacity="0.1" />
        ))}

        {/* SIM zone background */}
        <rect x={simStartX} y="0" width={w - simStartX} height={h} fill="white" opacity="0.03" />

        {/* Future rounds (dotted area) */}
        {lastColIdx >= 0 && lastColIdx < n - 1 && (
          <rect x={xForIdx(lastColIdx)} y="0" width={w - xForIdx(lastColIdx)} height={h} fill="white" opacity="0.02" />
        )}

        {/* SIM boundary line */}
        <line x1={simStartX} y1="0" x2={simStartX} y2={h} stroke="white" strokeWidth="0.5" opacity="0.3" strokeDasharray="3,3" />

        {/* Dangerous Proximity zone (red/amber glow between converging lines) */}
        <g fill="#ff4444" opacity="0.15" dangerouslySetInnerHTML={{ __html: proximityPaths.join('') }} />

        {/* Columbia line */}
        <polyline fill="none" stroke="#3A6B9F" strokeWidth="2.5" opacity="0.85" points={toPoints(allColRaw)} />
        {/* Cathay line */}
        <polyline fill="none" stroke="#C4A030" strokeWidth="2.5" opacity="0.85" points={toPoints(allCatRaw)} />

        {/* Country labels at start */}
        <text x="3" y={yForVal(allColRaw[0]!) - 6} fill="#3A6B9F" fontSize="20" fontWeight="bold" opacity="0.9">Columbia</text>
        <text x="3" y={yForVal(allCatRaw[0]!) + 16} fill="#C4A030" fontSize="20" fontWeight="bold" opacity="0.9">Cathay</text>

        {/* Current round marker (pulsing dot) */}
        {lastColIdx >= 0 && (
          <>
            <circle cx={xForIdx(lastColIdx)} cy={yForVal(allColRaw[lastColIdx]!)} r="4" fill="#3A6B9F" opacity="0.9">
              <animate attributeName="r" values="3;5;3" dur="2s" repeatCount="indefinite" />
            </circle>
            <circle cx={xForIdx(lastCatIdx)} cy={yForVal(allCatRaw[lastCatIdx]!)} r="4" fill="#C4A030" opacity="0.9">
              <animate attributeName="r" values="3;5;3" dur="2s" repeatCount="indefinite" />
            </circle>
          </>
        )}

        {/* Power values + trend arrows at current position */}
        {lastColIdx >= 0 && (() => {
          const colNow = allColRaw[lastColIdx]!
          const catNow = allCatRaw[lastCatIdx]!
          const colPrev = lastColIdx > 0 ? allColRaw[lastColIdx - 1] : null
          const catPrev = lastCatIdx > 0 ? allCatRaw[lastCatIdx - 1] : null
          const colArrow = colPrev !== null ? (colNow > colPrev ? '\u25B2' : colNow < colPrev ? '\u25BC' : '') : ''
          const catArrow = catPrev !== null ? (catNow > catPrev ? '\u25B2' : catNow < catPrev ? '\u25BC' : '') : ''
          const colColor = colPrev !== null ? (colNow > colPrev ? '#4ade80' : colNow < colPrev ? '#f87171' : '#3A6B9F') : '#3A6B9F'
          const catColor = catPrev !== null ? (catNow > catPrev ? '#4ade80' : catNow < catPrev ? '#f87171' : '#C4A030') : '#C4A030'
          return (
            <>
              <text x={xForIdx(lastColIdx) + 6} y={yForVal(colNow) - 2} fill={colColor} fontSize="7" fontWeight="bold" opacity="0.9">{colNow} {colArrow}</text>
              <text x={xForIdx(lastCatIdx) + 6} y={yForVal(catNow) + 8} fill={catColor} fontSize="7" fontWeight="bold" opacity="0.9">{catNow} {catArrow}</text>
            </>
          )
        })()}

        {/* Year/round labels at bottom */}
        {allLabels.map((l, i) => {
          // Show every other historical label to avoid crowding, always show SIM labels
          const isSimLabel = l.startsWith('R')
          const isKeyYear = ['2006','2010','2014','2018','2022','2026'].includes(l)
          if (!isSimLabel && !isKeyYear) return null
          return (
            <text key={i} x={i * (w / (n - 1))} y={h + 12} fill={isSimLabel ? 'white' : 'white'} fontSize="7" opacity={isSimLabel ? 0.6 : 0.3} textAnchor="middle" fontWeight={isSimLabel ? 'bold' : 'normal'}>
              {l}
            </text>
          )
        })}

        {/* "SIM" label above the sim zone */}
        {simLabels.length > 0 && (
          <text x={(simStartX + w) / 2} y="8" fill="white" fontSize="7" opacity="0.25" textAnchor="middle" fontWeight="bold">
            SIM
          </text>
        )}
      </svg>
    </div>
  )
}
